export const APP_ROUTES = {
  root: "/",
  authCallback: "/auth/callback",
  resetPassword: "/auth/reset-password",
  onboarding: "/onboarding",
  library: "/library",
  savedVocab: "/saved-vocab",
  history: "/history",
  settings: "/settings",
} as const;

export const RETURN_TARGET_STORAGE_KEY = "pagebunny:return-to";

const INERT_ORIGIN = "https://pagebunny.invalid";
const SECTION_PREFIX = `${APP_ROUTES.library}/sections/`;

export const librarySectionPath = (sectionId: string): string =>
  `${APP_ROUTES.library}/sections/${encodeURIComponent(sectionId)}`;

function containsControlCharacter(target: string): boolean {
  for (let index = 0; index < target.length; index += 1) {
    const codeUnit = target.charCodeAt(index);
    if (codeUnit <= 0x1f || codeUnit === 0x7f) {
      return true;
    }
  }

  return false;
}

function hasSafeRawPathShape(target: string): boolean {
  return (
    target.startsWith("/") &&
    !target.startsWith("//") &&
    !target.includes("\\") &&
    !containsControlCharacter(target)
  );
}

function parseInternalTarget(target: string): URL | null {
  if (!hasSafeRawPathShape(target)) {
    return null;
  }

  try {
    const url = new URL(target, INERT_ORIGIN);
    return url.origin === INERT_ORIGIN ? url : null;
  } catch {
    return null;
  }
}

function rawPathname(target: string): string {
  const suffixStart = target.search(/[?#]/);
  return suffixStart === -1 ? target : target.slice(0, suffixStart);
}

function isSectionPath(pathname: string): boolean {
  if (!pathname.startsWith(SECTION_PREFIX)) {
    return false;
  }

  const encodedSectionId = pathname.slice(SECTION_PREFIX.length);
  if (!encodedSectionId || encodedSectionId.includes("/")) {
    return false;
  }

  try {
    return decodeURIComponent(encodedSectionId).length > 0;
  } catch {
    return false;
  }
}

function getSessionStorage(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

function removeStoredReturnTarget(): void {
  try {
    getSessionStorage()?.removeItem(RETURN_TARGET_STORAGE_KEY);
  } catch {
    // Storage access is optional for navigation safety.
  }
}

export function isProtectedAppPath(pathname: string): boolean {
  return (
    pathname === APP_ROUTES.library ||
    pathname === APP_ROUTES.savedVocab ||
    pathname === APP_ROUTES.history ||
    pathname === APP_ROUTES.settings ||
    isSectionPath(pathname)
  );
}

export function normalizeReturnTarget(rawTarget: string | null | undefined): string {
  if (typeof rawTarget !== "string") {
    return APP_ROUTES.library;
  }

  const parsedTarget = parseInternalTarget(rawTarget);
  return parsedTarget &&
    rawPathname(rawTarget) === parsedTarget.pathname &&
    isProtectedAppPath(parsedTarget.pathname)
    ? rawTarget
    : APP_ROUTES.library;
}

export function storeReturnTarget(rawTarget: string): string {
  const target = normalizeReturnTarget(rawTarget);
  if (target === APP_ROUTES.library && rawTarget !== APP_ROUTES.library) {
    removeStoredReturnTarget();
    return target;
  }

  try {
    getSessionStorage()?.setItem(RETURN_TARGET_STORAGE_KEY, target);
  } catch {
    // A stored return target is a convenience, never a navigation dependency.
  }

  return target;
}

export function peekReturnTarget(): string {
  let storedTarget: string | null;

  try {
    storedTarget = getSessionStorage()?.getItem(RETURN_TARGET_STORAGE_KEY) ?? null;
  } catch {
    return APP_ROUTES.library;
  }

  const target = normalizeReturnTarget(storedTarget);
  if (target === APP_ROUTES.library && storedTarget !== APP_ROUTES.library) {
    removeStoredReturnTarget();
  }

  return target;
}

export function consumeReturnTarget(): string {
  const target = peekReturnTarget();
  removeStoredReturnTarget();
  return target;
}

export function clearReturnTarget(): void {
  removeStoredReturnTarget();
}
